import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from '@aws-amplify/core';
import {
    Badge,
    Group,
    Text,
} from '@mantine/core';

import {formatTimestamp} from '../../../utils/TableTimestamps.tsx';
import {Persona} from '../types.ts';
import config from '../config';

export const usePersonasTableColumns = () => {

    const columns: DataTableColumn<Persona>[] = [
        {
            accessor: 'name',
            title: I18n.get('Name'),
            width: '1fr',
            render: (item: Persona) => (
                <NavLink to={`${config.basePath}/${item.id}`}>{item.name}</NavLink>
            ),
        },
        {
            accessor: 'description',
            title: I18n.get('Description'),
            width: '1fr',
            render: (item: Persona) => item.description,
        },
        {
            // What the executor actually runs. Shown as a COUNT: resolving ids to
            // test codes would need the definitions catalog loaded per row, and
            // the detail belongs on the persona page rather than in a list cell.
            accessor: 'testDefinitionIds',
            title: I18n.get('Test Definitions'),
            width: '150px',
            render: (item: Persona) => {
                const count = (item.testDefinitionIds ?? []).length;
                if (count === 0) {
                    return <Text size={'sm'} c={'dimmed'}>Not set</Text>;
                }
                return <Badge variant={'light'}>{count}</Badge>;
            },
        },
        {
            // ⚠️ DEPRECATED (FNP-968) — no longer read by the executor.
            accessor: 'actionCodes',
            title: I18n.get('Action Codes (deprecated)'),
            width: '1fr',
            render: (item: Persona) => {
                const codes = item.actionCodes ?? [];
                if (codes.length === 0) {
                    return <Text size={'sm'} c={'dimmed'}>Not set</Text>;
                }
                return (
                    <Group gap={'xs'}>
                        {codes.map(entry => (
                            <Badge key={entry.actionCode} variant={'light'}>
                                {entry.actionCode} ({entry.weight})
                            </Badge>
                        ))}
                    </Group>
                );
            },
        },
        {
            accessor: 'lifecycle',
            title: I18n.get('Wake / Lifespan'),
            width: '1fr',
            render: (item: Persona) => {
                if (!item.lifecycle) {
                    return <Text size={'sm'} c={'dimmed'}>Not set</Text>;
                }
                return `every ${item.lifecycle.wakeCadenceHours}h / ${item.lifecycle.expectedLifespanDays}d`;
            },
        },
        {
            accessor: 'created',
            title: I18n.get('Created'),
            width: '1fr',
            render: (item: Persona) => formatTimestamp(item.created),
        },
    ];

    return {
        columns,
    };
};
